import * as R from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Table } from 'reactstrap';
import { withMenu } from '../../../reactutils/withMenu';
import { withTitle } from '../../../reactutils/withTitle';
import { WebUsage, WebUsageSummary } from '../../../services/account';
import { LoadingIndicatorSpinner } from '../../common/LoadingIndicatorSpinner';
import { Section, SectionHeading } from '../../common/Section';
import { TruncateText } from '../../common/TruncateText';
import { DateTime } from '../../DateTime';
import { MenuSysAdmin } from '../../MenuSysAdmin';
import { ServerType } from '../../System/systemConfig';
import { sysadminLoadWebUsage, sysadminLoadWebUsageDetails } from './actions';
import { webUsageDetailSelector, webUsageSummarySelector } from './selectors';

interface Props {
    serverName: ServerType;
}
const WebUsageComp: FC<Props> = props => {
    const dispatch = useDispatch();

    const [showDetails, setShowDetails] = useState(false);

    const entities = useSelector(webUsageSummarySelector(props.serverName) || []);
    const details = useSelector(webUsageDetailSelector(props.serverName) || []);

    useEffect(() => {
        dispatch(sysadminLoadWebUsage(props.serverName));
    }, [dispatch, props.serverName]);

    useEffect(() => {
        if (showDetails) {
            dispatch(sysadminLoadWebUsageDetails(props.serverName));
        }
    }, [dispatch, props.serverName, showDetails]);

    const renderWebUsage = (wu: WebUsageSummary, index: number) => {
        return (
            <tr key={index}>
                <td>{wu.tenant}</td>
                <td>{wu.clientVersion}</td>
                <td>{wu.anonymous ? 'ja' : 'nein'}</td>
                <td>{wu.clientCount}</td>
            </tr>
        );
    };

    const renderWebUsageDetails = (wu: WebUsage, index: number) => {
        return (
            <tr key={index}>
                <td>{wu.tenant}</td>
                <td>{wu.clientVersion}</td>
                <td>{wu.clientTag}</td>
                <td>
                    <DateTime date={wu.lastAccess} />
                </td>
                <td>
                    <TruncateText text={wu.accessToken || ''} maxLength={50} />
                </td>
            </tr>
        );
    };

    return (
        <>
            <Section>
                <SectionHeading>{props.serverName}</SectionHeading>
                <Button
                    color="secondary"
                    className="m-2"
                    outline={false}
                    onClick={() => dispatch(sysadminLoadWebUsage(props.serverName))}
                >
                    Reload
                </Button>
                {!showDetails && (
                    <Button color="secondary" className="m-2" outline={false} onClick={() => setShowDetails(true)}>
                        Show Details
                    </Button>
                )}
                {showDetails && (
                    <Button color="secondary" className="m-2" outline={false} onClick={() => setShowDetails(false)}>
                        Hide Details
                    </Button>
                )}
                <Table>
                    <thead>
                        <tr>
                            <th>Tenant</th>
                            <th>Client-Version</th>
                            <th>Anonymous</th>
                            <th>Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entities && entities.map((e, index) => renderWebUsage(e, index))}
                        {!entities && (
                            <tr>
                                <td>
                                    <LoadingIndicatorSpinner />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {showDetails && (
                    <Container className="bg-warning pt-2">
                        <h5>Details</h5>
                        <small>
                            <Table striped={true}>
                                <thead>
                                    <th>Tenant</th>
                                    <th>Client-Version</th>
                                    <th>Client-tag</th>
                                    <th>Last Access</th>
                                    <th>AccessToken</th>
                                </thead>
                                <tbody>{details && details.map((e, index) => renderWebUsageDetails(e, index))}</tbody>
                            </Table>
                        </small>
                    </Container>
                )}
            </Section>
        </>
    );
};

const WebUsageComponent: FC<{}> = () => {
    return (
        <>
            <p>The page shows the active clients per server in the last 20 minutes.</p>
            <WebUsageComp serverName="account" />
            <WebUsageComp serverName="betting" />
            <WebUsageComp serverName="image" />
            <WebUsageComp serverName="playlist" />
        </>
    );
};

export const WebUsageScreen = R.compose(withMenu(<MenuSysAdmin />), withTitle('Rest-Clients'))(WebUsageComponent);
